import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Close } from '@mui/icons-material';
import Tooltip from '../utils/Tooltip';
import BuildScore from '../build/BuildScore';
import CriteriaIconImg from './CriteriaIconImg';
import { formatWeightValue, WEIGHT_DEFAULT } from '../../core/criteriaWeights';
import './CriteriaList.css';

const TXT_EMPTY = { fr: 'Aucun critere selectionne', en: 'No criteria selected', de: 'Keine Kriterien ausgewählt', es: 'Ningún criterio seleccionado', pt: 'Nenhum critério selecionado' };
const TXT_REMOVE = { fr: 'Retirer', en: 'Remove', de: 'Entfernen', es: 'Quitar', pt: 'Remover' };

const CriteriaIcon = ({ criterion, lang = 'fr', disabled = false }) => {
    const label = criterion?.label || '';
    const parsedLabel = String(label).match(/^(.*?)(\s*(?:>=|≥)\s*.+)$/);
    const tooltipMainLabel = parsedLabel ? parsedLabel[1].trim() : label;
    const inlineTargetLabel = parsedLabel ? parsedLabel[2].trim() : null;
    const tooltipTargetLabel = criterion?.targetLabel || inlineTargetLabel;
    const spellLineIndicator = criterion?.spellLineIndicator || null;
    // ×1 is the default every criterion carries, so only a changed weight is worth a line.
    const weight = Number(criterion?.weight);
    const weightLabel = Number.isFinite(weight) && weight !== WEIGHT_DEFAULT
        ? `×${formatWeightValue(weight)}`
        : null;
    const removeLabel = TXT_REMOVE[lang] || TXT_REMOVE.en;
    const tooltipContent = (
        <div className="criteria-list-item-tooltip row">
            <CriteriaIconImg
                src={criterion?.icon}
                spark={criterion?.spark}
                className={`criteria-list-item-tooltip__icon${criterion?.kind ? ` criteria-list-item-tooltip__icon--${criterion.kind}` : ''}`}
                aria-hidden="true"
            />
            <span className="criteria-list-item-tooltip__text">{tooltipMainLabel}</span>
            {spellLineIndicator ? (
                <span className="criteria-list-item-tooltip__line-indicator">{spellLineIndicator}</span>
            ) : null}
            {tooltipTargetLabel ? (
                <span className="criteria-list-item-tooltip__target">{tooltipTargetLabel}</span>
            ) : null}
            {weightLabel ? (
                <span className="criteria-list-item-tooltip__weight">{weightLabel}</span>
            ) : null}
        </div>
    );
    return (
        <Tooltip content={tooltipContent}>
            <button
                type="button"
                className="criteria-list-item"
                onClick={criterion?.onRemove}
                disabled={disabled || !criterion?.onRemove}
                aria-label={`${removeLabel} ${label}`.trim()}
            >
                <span className="criteria-list-item__icon-slot">
                    <CriteriaIconImg
                        src={criterion?.icon}
                        spark={criterion?.spark}
                        className={`criteria-list-item__icon${criterion?.kind ? ` criteria-list-item__icon--${criterion.kind}` : ''}`}
                        aria-hidden="true"
                        fallback={(
                            <span className="criteria-list-item__fallback" aria-hidden="true">
                                {(label || '?').slice(0, 1)}
                            </span>
                        )}
                    />
                    <Close className="criteria-list-item__close" fontSize="small" />
                </span>
            </button>
        </Tooltip>
    );
};

const CriteriaList = ({
    lang = 'fr',
    activeCriteria = [],
    score = null,
    scoreColor,
    breakdown = null,
    spellLineIndicatorMap = null,
    disabled = false,
    showScore = true,
    className = '',
}) => {
    const itemsLabelRef = useRef(null);
    const rafRef = useRef(null);
    const [overflowState, setOverflowState] = useState({ hasOverflow: false, canScrollLeft: false, canScrollRight: false });

    const measureOverflow = useCallback(() => {
        const el = itemsLabelRef.current;
        if (!el) {
            return;
        }
        const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        const hasOverflow = maxLeft > 1;
        const canScrollLeft = hasOverflow && el.scrollLeft > 1;
        const canScrollRight = hasOverflow && el.scrollLeft < (maxLeft - 1);
        setOverflowState((prev) => (
            prev.hasOverflow === hasOverflow
                && prev.canScrollLeft === canScrollLeft
                && prev.canScrollRight === canScrollRight
                ? prev
                : { hasOverflow, canScrollLeft, canScrollRight }
        ));
    }, []);

    const scheduleOverflowMeasure = useCallback(() => {
        if (rafRef.current != null) return;
        rafRef.current = window.requestAnimationFrame(() => {
            rafRef.current = null;
            measureOverflow();
        });
    }, [measureOverflow]);

    useEffect(() => {
        const el = itemsLabelRef.current;
        if (!el) {
            return undefined;
        }

        const onWheel = (event) => {
            event.preventDefault();
            if (!overflowState.hasOverflow) {
                return;
            }
            const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (!horizontalDelta) {
                return;
            }
            el.scrollLeft += horizontalDelta;
        };

        el.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            el.removeEventListener('wheel', onWheel);
        };
    }, [overflowState.hasOverflow]);

    useEffect(() => {
        scheduleOverflowMeasure();
    }, [activeCriteria, scheduleOverflowMeasure]);

    useEffect(() => {
        const el = itemsLabelRef.current;
        if (!el) return undefined;

        let resizeObserver = null;
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                scheduleOverflowMeasure();
            });
            resizeObserver.observe(el);
        } else {
            window.addEventListener('resize', scheduleOverflowMeasure);
        }

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener('resize', scheduleOverflowMeasure);
            }
            if (rafRef.current != null) {
                window.cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [scheduleOverflowMeasure]);

    return (
        <div className={`criteria-list row${className ? ` ${className}` : ''}`}>
            <div className="criteria-list-list row">
                <div className="criteria-list-items row">
                    <div
                        ref={itemsLabelRef}
                        onScroll={scheduleOverflowMeasure}
                        className={`criteria-list-items-label row${overflowState.hasOverflow ? ' has-overflow' : ''}${overflowState.canScrollLeft ? ' can-scroll-left' : ''}${overflowState.canScrollRight ? ' can-scroll-right' : ''}`}
                    >
                        {activeCriteria.length ? activeCriteria.map((criterion) => (
                            <CriteriaIcon
                                key={criterion.key}
                                criterion={{
                                    ...criterion,
                                    spellLineIndicator: criterion?.spellCriterionSig
                                        ? spellLineIndicatorMap?.get?.(criterion.spellCriterionSig)
                                        : null,
                                }}
                                lang={lang}
                                disabled={disabled}
                            />
                        )) : (
                            <span className="criteria-list-empty">
                                {TXT_EMPTY[lang] || TXT_EMPTY.en}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {showScore ? (
                <div className="criteria-list-score">
                    <BuildScore
                        score={score}
                        lang={lang}
                        color={scoreColor}
                        breakdown={breakdown}
                        spellLineIndicatorMap={spellLineIndicatorMap}
                        compact
                        bordered
                    />
                </div>
            ) : null}
        </div>
    );
};

export default CriteriaList;
